import * as Tooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import {
  SHORTCUT_DEFINITIONS,
  isMacPlatform,
  shortcutDisplay,
  type ShortcutId,
} from '@/app/shortcuts';

type PanelActionTooltipProps = {
  /** Plain-text label shown in the tooltip body. */
  label: string;
  /** Optional shortcut id — when provided, the tooltip appends a key combo. */
  shortcutId?: ShortcutId;
  children: ReactNode;
  /** Override delay (ms) before the tooltip appears. Default 250ms. */
  delayMs?: number;
};

/**
 * Tooltip wrapper for panel header buttons. Renders the action label and,
 * when a `shortcutId` is provided, the matching key combo glyphs so power
 * users learn the shortcuts as they hover.
 */
export function PanelActionTooltip({
  label,
  shortcutId,
  children,
  delayMs = 250,
}: PanelActionTooltipProps) {
  const def = shortcutId
    ? SHORTCUT_DEFINITIONS.find((d) => d.id === shortcutId)
    : undefined;
  const tokens = def ? shortcutDisplay(def, isMacPlatform()) : null;

  return (
    <Tooltip.Provider delayDuration={delayMs} skipDelayDuration={100}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            sideOffset={6}
            className="z-50 flex items-center gap-2 rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow"
          >
            <span>{label}</span>
            {tokens && (
              <span className="flex items-center gap-0.5" aria-hidden="true">
                {tokens.map((token, idx) => (
                  <kbd
                    key={`${shortcutId}-${idx}`}
                    className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground"
                  >
                    {token}
                  </kbd>
                ))}
              </span>
            )}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
